import React, { Component, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { Modal } from 'react-bootstrap';
import PastRolesDisplay from '../common/PastRolesDisplay';
import Loading from '../common/Loading';
import { postRequest } from '../../requestUtils';
import { analyticsTrack } from '../../analyticsUtils';
import { date } from '../../dateUtils';
import { orderByStartDate } from '../../arrayUtils';
import './AddProposalPastRoles.scss';

const PastRolesQuestion = lazy(() => import('../common/PastRolesQuestion'));

class AddProposalPastRoles extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showModal: false,
      errors: [],
      pastRoles: props.pastRoles,
      proposalPastRolesIds: props.selectedPastRolesIds,
      selectedPastRolesIds: props.selectedPastRolesIds,
      newPastRoles: [],
    };
    this.handleShowModal = this.handleShowModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.toggleLink = this.toggleLink.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.renderSelectPastRoles = this.renderSelectPastRoles.bind(this);
  }

  handleShowModal() {
    this.setState({ showModal: true });
  }

  handleCloseModal() {
    this.setState({ showModal: false });
  }

  toggleLink(currentInviteStatus, pastRole) {
    const { selectedPastRolesIds } = this.state;
    if (currentInviteStatus) {
      this.setState({
        selectedPastRolesIds: selectedPastRolesIds.filter((i) => i !== pastRole.id),
      }, () => { this.handleSubmit([]); });
    } else {
      const newSelectedPastRoles = update(selectedPastRolesIds, { $push: [pastRole.id] });
      this.setState(
        { selectedPastRolesIds: newSelectedPastRoles },
        () => { this.handleSubmit([]); },
      );
    }
  }

  handleSubmit(newPastRoles) {
    const { csrfToken, proposalPastRolesUrl, proposal } = this.props;
    const { selectedPastRolesIds, pastRoles } = this.state;
    return postRequest(
      proposalPastRolesUrl,
      {
        selected_past_roles_ids: selectedPastRolesIds,
        new_past_roles: newPastRoles,
      },
      csrfToken,
    ).then((response) => {
      this.setState({
        pastRoles: pastRoles.concat(response.data.new_past_roles),
        proposalPastRolesIds: response.data.selected_past_roles_ids,
        selectedPastRolesIds: response.data.selected_past_roles_ids,
        newPastRoles: [],
      });
      analyticsTrack('Submitted Proposal Past Roles', {
        resource_id: proposal.id,
      });
    });
  }

  renderDisplay() {
    const { pastRoles, proposalPastRolesIds } = this.state;
    const { isRep, canEdit } = this.props;
    if ((proposalPastRolesIds.length === 0 || pastRoles.length === 0) && canEdit) {
      return (
        <div className="body-text-2">
          <h1 className={`medium-weight mb-2 ${isRep && 'rep-header'}`}>
            <i className="fa fa-gem" />
            Relevant Past Roles
          </h1>
          <div className="mb-4">
            {`Highlight related past roles to ensure the employer of your
            relevant experience.`}
          </div>
          <div>
            <a
              tabIndex={0}
              role="link"
              className="add-proposal-past-roles"
              onClick={this.handleShowModal}
            >
              <i className="fa fa-plus" />
              Add relevant past roles
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="body-text-2">
        <h1 className={`medium-weight mb-2 ${(isRep || !canEdit) && 'rep-header'}`}>
          <i className="fa fa-gem" />
          Relevant Past Roles
          { canEdit
            && (
            <a
              tabIndex={0}
              role="link"
              className="align edit-proposal-past-roles"
              onClick={this.handleShowModal}
            >
              Edit
            </a>
            )}
        </h1>
        <div className="mt-3">
          <PastRolesDisplay
            pastRoles={pastRoles.filter((pr) => proposalPastRolesIds.includes(pr.id))}
            fullView={isRep || canEdit}
            onProposalPage
          />
        </div>
      </div>
    );
  }

  renderModalSubtext() {
    const { pastRoles } = this.state;
    if (pastRoles.length === 0) {
      return 'Organize and edit past roles to highlight your experiences relevant to this project.';
    }
    return 'Select the relevant past roles for this project. You can always edit your past role history in My Profile.';
  }

  renderSelectPastRoles() {
    const { pastRoles, selectedPastRolesIds } = this.state;
    return (
      <div className="past-roles">
        { orderByStartDate(pastRoles).map((pastRole) => (
          <div
            key={pastRole.title}
            tabIndex={0}
            role="button"
            className="align select-past-role body-text-2 mb-3"
            onClick={() => this.toggleLink(selectedPastRolesIds.includes(pastRole.id), pastRole)}
          >
            <div className="past-roles-info">
              <div className="fs-hide medium-weight">
                {pastRole.title}
              </div>
              <div className="fs-hide">
                {pastRole.role}
              </div>
              <div className="fs-hide">
                {pastRole.company_name}
              </div>
              <div className="mute fs-hide">
                {date(pastRole.start_date, pastRole.end_date, pastRole.current)}
              </div>
            </div>
            <div className="switch">
              <input
                type="checkbox"
                checked={selectedPastRolesIds.includes(pastRole.id)}
                readOnly
              />
              <span className="slider round" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  renderNewPastRoles() {
    const { selectedPastRolesIds, newPastRoles } = this.state;
    return (
      <Suspense fallback={<Loading />}>
        <PastRolesQuestion
          questionKey="past_roles"
          newPastRoles={newPastRoles}
          handleChange={this.handleSubmit}
          closeModal={this.handleCloseModal}
          showBorder={selectedPastRolesIds.length > 0}
          disableSubmit={selectedPastRolesIds.length === 0}
          onProposalPage
        />
      </Suspense>
    );
  }

  render() {
    const {
      selectedPastRolesIds,
      pastRoles,
      showModal,
    } = this.state;
    return (
      <div className="style-guide relevant-past-roles">
        {this.renderDisplay()}
        <Modal
          dialogClassName="proposal-past-roles-modal-size"
          className="style-guide"
          show={showModal}
          onHide={this.handleShowModal}
          onEscapeKeyDown={this.handleShowModal}
        >
          <Modal.Body>
            <div
              onClick={this.handleCloseModal}
              role="button"
              tabIndex={0}
              className="close-modal"
            >
              <i className="fas fa-times fa-lg" />
            </div>
            <div className="proposal-past-roles-modal body-text-2">
              <h2 className="mb-2">
                Select relevant past roles for this project.
              </h2>
              <div className="mb-4 subtext">
                You may update your past roles in My Profile to present
                the most up to date information on this proposal.
              </div>
              <div className="add-new-past-role">
                {(selectedPastRolesIds.length + pastRoles.length) > 0
                  && this.renderSelectPastRoles()}
                {this.renderNewPastRoles()}
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}

AddProposalPastRoles.propTypes = {
  proposal: PropTypes.shape({}).isRequired,
  csrfToken: PropTypes.string.isRequired,
  proposalPastRolesUrl: PropTypes.string.isRequired,
  pastRoles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedPastRolesIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  isRep: PropTypes.bool.isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default AddProposalPastRoles;
